<!-- Copyright (c) 2009-2021. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
<div class="fluorescence-panel">
  <h1>{{$t('fluorescence-viewer')}}</h1>

  <b-switch v-model="enabled" class="fluorescence-toggle">
    {{$t('enable-fluorescence-viewer')}}
  </b-switch>

  <p class="help">
    {{$t('fluorescence-viewer-help')}}
  </p>

  <div v-if="enabled" class="mode-section">
    <div class="section-label">{{$t('fluorescence-mode')}}</div>
    <div class="buttons has-addons mode-selector" role="radiogroup">
      <label class="button is-small" :class="{'is-link': mode === 'composite'}">
        <input class="is-sr-only" type="radio" v-model="mode" value="composite" />
        {{$t('fluorescence-mode-composite')}}
      </label>
      <label class="button is-small" :class="{'is-link': mode === 'single'}">
        <input class="is-sr-only" type="radio" v-model="mode" value="single" />
        {{$t('fluorescence-mode-single')}}
      </label>
    </div>
  </div>

  <div v-if="enabled && mode === 'composite'" class="channel-settings composite-mode">
    <div v-for="channel in channels" :key="channel.index" class="channel-card">
      <div class="channel-header">
        <label class="checkbox channel-visibility">
          <input
            type="checkbox"
            :checked="channel.settings.visible"
            @change="setChannelVisible(channel.index, $event.target.checked)"
          />
          C{{channel.index}}
        </label>

        <input
          class="channel-color"
          type="color"
          :value="channel.settings.color"
          @input="setChannelColor(channel.index, $event.target.value)"
        />
      </div>

      <div class="channel-range-label">
        {{$t('fluorescence-window')}}: {{channel.settings.min}} - {{channel.settings.max}}
      </div>
      <cytomine-slider
        :value="[channel.settings.min, channel.settings.max]"
        :min="0"
        :max="255"
        class="channel-range"
        @input="setChannelRange(channel.index, $event)"
      />
    </div>

    <div class="actions">
      <button class="button is-small" @click="resetComposite()">{{$t('button-reset')}}</button>
    </div>
  </div>

  <div v-if="enabled && mode === 'single'" class="single-channel-mode">
    <p class="single-help">
      {{$t('fluorescence-single-channel-help')}}
    </p>

    <div class="section-label">{{$t('fluorescence-channel')}}</div>
    <div class="buttons has-addons channel-selector" role="radiogroup">
      <button
        v-for="channel in channels"
        :key="channel.index"
        class="button is-small channel-button"
        :class="{'is-link': activeChannel === channel.index}"
        @click="setActiveChannel(channel.index)"
      >
        C{{channel.index}}
      </button>
    </div>

    <div class="channel-range-label single-range-label">
      {{$t('fluorescence-window')}} C{{activeChannel}}: {{activeSingleSettings.min}} - {{activeSingleSettings.max}}
    </div>
    <cytomine-slider
      :value="[activeSingleSettings.min, activeSingleSettings.max]"
      :min="0"
      :max="255"
      class="channel-range single-range"
      @input="setSingleChannelRange(activeChannel, $event)"
    />

    <div class="actions">
      <button class="button is-small" @click="resetSingle()">{{$t('button-reset')}}</button>
    </div>
  </div>
</div>
</template>

<script>
import CytomineSlider from '@/components/form/CytomineSlider';
import {DEFAULT_FLUORESCENCE_COLORS, DEFAULT_FLUORESCENCE_MAX} from '@/utils/fluorescence';

export default {
  name: 'fluorescence-panel',
  components: {CytomineSlider},
  props: {
    index: String
  },
  computed: {
    imageModule() {
      return this.$store.getters['currentProject/imageModule'](this.index);
    },
    imageWrapper() {
      return this.$store.getters['currentProject/currentViewer'].images[this.index];
    },
    image() {
      return this.imageWrapper.imageInstance;
    },
    fluorescence() {
      return this.imageWrapper.colors.fluorescence;
    },
    enabled: {
      get() {
        return this.fluorescence.enabled;
      },
      async set(value) {
        if(value) {
          await this.$store.dispatch(this.imageModule + 'enableFluorescenceForCurrentPlane');
        }
        else {
          this.$store.commit(this.imageModule + 'setFluorescenceEnabled', false);
        }
      }
    },
    mode: {
      get() {
        return this.fluorescence.mode || 'composite';
      },
      set(mode) {
        this.$store.commit(this.imageModule + 'setFluorescenceMode', mode);
      }
    },
    activeChannel: {
      get() {
        return this.fluorescence.activeChannel || 0;
      },
      set(channel) {
        this.$store.commit(this.imageModule + 'setFluorescenceActiveChannel', {
          channel,
          nbChannels: this.image.channels
        });
      }
    },
    activeSingleSettings() {
      return this.singleChannels[this.activeChannel] || this.defaultSingleChannelSettings();
    },
    singleChannels() {
      return this.fluorescence.singleChannels || {};
    },
    channels() {
      let channels = [];
      for(let channel = 0; channel < this.image.channels; channel++) {
        channels.push({
          index: channel,
          settings: this.fluorescence.channels[channel] || this.defaultChannelSettings(channel)
        });
      }
      return channels;
    }
  },
  methods: {
    defaultChannelSettings(channel) {
      return {
        visible: true,
        color: DEFAULT_FLUORESCENCE_COLORS[channel % DEFAULT_FLUORESCENCE_COLORS.length],
        min: 0,
        max: DEFAULT_FLUORESCENCE_MAX
      };
    },
    defaultSingleChannelSettings() {
      return {
        min: 0,
        max: DEFAULT_FLUORESCENCE_MAX
      };
    },
    setActiveChannel(channel) {
      this.activeChannel = channel;
    },
    setChannelVisible(channel, visible) {
      this.$store.commit(this.imageModule + 'setFluorescenceChannelVisible', {channel, visible});
    },
    setChannelColor(channel, color) {
      this.$store.commit(this.imageModule + 'setFluorescenceChannelColor', {channel, color});
    },
    setChannelRange(channel, range) {
      this.$store.commit(this.imageModule + 'setFluorescenceChannelRange', {channel, range});
    },
    setSingleChannelRange(channel, range) {
      this.$store.commit(this.imageModule + 'setFluorescenceSingleChannelRange', {channel, range});
    },
    resetComposite() {
      this.$store.commit(this.imageModule + 'resetFluorescenceChannels', this.image.channels);
    },
    resetSingle() {
      this.$store.commit(this.imageModule + 'resetFluorescenceSingleChannel', this.activeChannel);
    }
  },
  created() {
    this.$store.commit(this.imageModule + 'initializeFluorescenceChannels', this.image.channels);
  }
};
</script>

<style lang="scss" scoped>
.fluorescence-panel {
  display: flex;
  flex-direction: column;
}

.fluorescence-toggle {
  margin-bottom: 0.5rem;
}

.help, .single-help {
  margin-bottom: 0.4rem;
  color: #666;
  font-size: 0.8em;
}

.mode-section {
  margin-bottom: 0.55rem;
}

.section-label {
  margin-bottom: 0.25rem;
  font-size: 0.78em;
  font-weight: 600;
  color: #444;
}

.mode-selector, .channel-selector {
  margin-bottom: 0.35rem;
  flex-wrap: wrap;
}

.mode-selector .button, .channel-button {
  margin-bottom: 0.25rem;
}

.channel-settings {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding-right: 0.35rem;
  padding-bottom: 1rem;
}

.channel-card {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  padding: 0.45rem 0 0.35rem;
}

.channel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.25rem;
}

.channel-visibility {
  font-weight: 600;
}

.channel-color {
  width: 3rem;
  height: 1.75rem;
  padding: 0;
  border: 1px solid #ccc;
  background: white;
}

.channel-range-label {
  margin-bottom: 0.2rem;
  font-size: 0.78em;
  color: #444;
}

.single-channel-mode {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding-right: 0.35rem;
  padding-bottom: 1rem;
}

.single-range-label {
  margin-top: 0.35rem;
}

.actions {
  margin-top: 0.6em;
  text-align: right;
}

>>> .vue-slider {
  margin-left: 1.25rem;
  margin-right: 2.75rem;
}
</style>
